import { memo, type ReactNode } from 'react';

import type { SelectProps } from 'antd';
import { Select as AntSelect } from 'antd';
import cn from 'classnames';

import cls from './input-select.module.css';

export interface InputSelectOption {
    label: ReactNode;
    value: string;
}

interface InputSelectProps
    extends Omit<SelectProps<string>, 'options' | 'mode'> {
    isBlock?: boolean;
    isMultiple?: boolean;
    isSorted?: boolean;
    options: InputSelectOption[] | string[];
}

export const InputSelect = memo((props: InputSelectProps) => {
    const {
        className,
        isBlock,
        isSorted = false,
        isMultiple = false,
        options,
        ...otherProps
    } = props;

    const normalizedOptions = options.map((option) => {
        if (typeof option === 'string') {
            return {
                label: option,
                value: option,
            };
        }

        return option;
    });

    const sortedOptions = isSorted
        ? [...normalizedOptions].sort((a, b) => {
              const labelA = typeof a.label === 'string' ? a.label : '';
              const labelB = typeof b.label === 'string' ? b.label : '';

              return labelA.localeCompare(labelB);
          })
        : normalizedOptions;

    return (
        <AntSelect<string>
            {...otherProps}
            className={cn(cls.root, className, {
                [cls.block]: isBlock,
                [cls.multiple]: isMultiple,
            })}
            getPopupContainer={(triggerNode) =>
                triggerNode.parentNode as HTMLElement
            }
            popupClassName={cls.popup}
            {...(isMultiple && { mode: 'multiple' })}
            options={sortedOptions}
        />
    );
});

InputSelect.displayName = 'InputSelect';
